/* eslint-disable max-lines */
import { cn } from "tailwind-variants";

import { useArtworkForm } from "@design-lab/entities/artwork";
import {
  currentPinningSessionAtom,
  designProjectPlacementIdsAtom,
  getPinningSessionForPlacement,
  pendingInitialMessageAtom,
  pinningEnabledArtworksAtom,
  pinningModeAtom,
  pinningSessionsAtom,
  useCreatePinningSession
} from "@design-lab/entities/pinning";
import { usePlacementKey } from "@design-lab/features/design-artwork/model/usePlacementKey";
import { useDebouncedCallback } from "@design-lab/shared/hooks/useDebounce";
import {
  trackDesignLabPinningModeEntered,
  trackDesignLabPinsCtaClicked,
  trackPinningSessionEntryInput
} from "@design-lab/shared/lib/analytics";
import {
  useDesignLabPinSessionRecording,
  useIsStaff,
  usePinBasedRedrawEnabled,
  usePinSessionForceMixpanelRecording
} from "@design-lab/shared/lib/config";
import { startPinSessionRecording } from "@design-lab/shared/lib/pinSessionRecording";
import { useToast } from "@design-lab/shared/ui";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { Textarea } from "@design-lab/shared/ui/textarea";
import { camelToSnakeCase } from "common/utils/helpers/cases";
import type { models } from "jiffy-api";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useRef, useState, type KeyboardEvent } from "react";
import { CompletedSessionIndicator } from "./CompletedSessionIndicator";

type RequestDesignChangesProps = {
  placementSide?: "front" | "back";
  currentValue?: models.cart.madeAdd.EnhancementTier | undefined;
  removeBackground?: boolean;
  canRemoveBackground?: boolean;
  editImageManual?: boolean;
};

export function RequestDesignChanges({
  placementSide = "front",
  currentValue,
  removeBackground = false,
  canRemoveBackground = true,
  editImageManual = false
}: RequestDesignChangesProps) {
  const { form } = useArtworkForm();
  const setPinningMode = useSetAtom(pinningModeAtom);
  const [pinningSessions, setPinningSessions] = useAtom(pinningSessionsAtom);
  const designProjectPlacementIds = useAtomValue(designProjectPlacementIdsAtom);
  const setCurrentPinningSession = useSetAtom(currentPinningSessionAtom);
  const setPendingInitialMessage = useSetAtom(pendingInitialMessageAtom);
  const { mutate: createPinningSession, isPending: isCreatingPinningSession } =
    useCreatePinningSession();
  const { error: showErrorToast } = useToast();
  const pinBasedRedrawEnabled = usePinBasedRedrawEnabled();
  const pinSessionRecordingEnabled = useDesignLabPinSessionRecording();
  const forceMixpanelRecording = usePinSessionForceMixpanelRecording();
  const isStaff = useIsStaff();
  const pinningEnabledArtworks = useAtomValue(pinningEnabledArtworksAtom);
  const [inputMessage, setInputMessage] = useState("");

  const recordingTriggered = useRef(false);

  const placementKey = usePlacementKey(placementSide);
  const placementData = form.watch(placementKey);
  const artworkIdForm = (placementData?.artworkId ? String(placementData.artworkId) : "") as string;

  const trackEntryInput = useCallback(
    (inputValue: string) => {
      if (artworkIdForm) {
        trackPinningSessionEntryInput({
          artworkId: artworkIdForm,
          placement: camelToSnakeCase(placementKey),
          inputValue
        });
      }
    },
    [artworkIdForm, placementKey]
  );

  const debouncedTrackEntryInput = useDebouncedCallback(trackEntryInput, 500);

  // Check if pinning is enabled for this artwork (based on ownership)
  // If artworkId is not in the map, assume enabled (backwards compatibility)
  const isPinningEnabled = artworkIdForm ? (pinningEnabledArtworks[artworkIdForm] ?? true) : true;

  const { designProjectPlacementId, session: sessionForPlacement } = getPinningSessionForPlacement({
    placementKey,
    designProjectPlacementIds,
    pinningSessions
  });
  const hasActiveSession = pinBasedRedrawEnabled && sessionForPlacement?.state === "active";
  const hasCompletedSession = sessionForPlacement?.state === "completed";

  // Determine the preview image name based on current selection
  // This matches the backend logic in dtf_print.rb artwork_preview_image_name
  const getPreviewImageName = useCallback((): string | undefined => {
    if (!currentValue) return undefined;

    if (currentValue === "print_original") {
      return "original_image";
    }

    if (currentValue === "full_enhancements") {
      if (editImageManual) {
        return "manual_edit_image";
      }
      if (removeBackground && canRemoveBackground) {
        return "image_no_background";
      }
      return "image_with_background";
    }

    return undefined;
  }, [currentValue, removeBackground, canRemoveBackground, editImageManual]);

  const handleRequestDesignerEdit = useCallback(
    (message: string) => {
      if (isCreatingPinningSession) return;
      const artworkId = artworkIdForm;
      if (!artworkId) return;
      if (!designProjectPlacementId) {
        showErrorToast({
          title: "Unable to start design instructions",
          description: "Please wait a moment and try again."
        });
        return;
      }

      const previewImageName = getPreviewImageName();

      trackDesignLabPinsCtaClicked({ artworkId, isStaff });

      // Reuse existing active session if one exists for this artwork
      const existingSession = sessionForPlacement;
      if (existingSession?.state === "active") {
        trackDesignLabPinningModeEntered({
          artworkId,
          sessionId: existingSession.sessionId,
          hasExistingPins: true,
          initialMessage: message,
          isStaff
        });
        setPendingInitialMessage(message);
        startPinSessionRecording({
          enabled: pinSessionRecordingEnabled
        });
        setCurrentPinningSession({
          designProjectPlacementId,
          artworkId,
          sessionId: existingSession.sessionId
        });
        setPinningMode(true);
        return;
      }

      const sessionParams = {
        designProjectPlacementId,
        artworkId,
        ...(previewImageName && { previewImageName })
      };

      createPinningSession(sessionParams, {
        onSuccess: (data: { sessionId: string }) => {
          trackDesignLabPinningModeEntered({
            artworkId,
            sessionId: data.sessionId,
            hasExistingPins: false,
            initialMessage: message,
            isStaff
          });
          setPendingInitialMessage(message);
          startPinSessionRecording({
            enabled: pinSessionRecordingEnabled
          });
          setPinningSessions((prev: typeof pinningSessions) => ({
            ...prev,
            [designProjectPlacementId]: {
              sessionId: data.sessionId,
              state: "active",
              summary: null
            }
          }));
          setCurrentPinningSession({
            designProjectPlacementId,
            artworkId,
            sessionId: data.sessionId
          });
          setPinningMode(true);
        },
        onError: () => {
          showErrorToast({
            title: "Unable to start design instructions",
            description: "Something went wrong. Please try again."
          });
        }
      });
    },
    [
      isCreatingPinningSession,
      artworkIdForm,
      designProjectPlacementId,
      getPreviewImageName,
      sessionForPlacement,
      createPinningSession,
      setPinningSessions,
      setCurrentPinningSession,
      setPinningMode,
      setPendingInitialMessage,
      pinSessionRecordingEnabled,
      showErrorToast,
      isStaff
    ]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.target;
      setInputMessage(value);
      debouncedTrackEntryInput(value);
      if (value.trim() && !recordingTriggered.current) {
        recordingTriggered.current = true;
        startPinSessionRecording({
          enabled: pinSessionRecordingEnabled,
          forceMixpanelRecording
        });
      }
    },
    [pinSessionRecordingEnabled, forceMixpanelRecording, debouncedTrackEntryInput]
  );

  const handleSubmitMessage = useCallback(() => {
    const trimmed = inputMessage.trim();
    if (!trimmed) return;
    handleRequestDesignerEdit(trimmed);
  }, [inputMessage, handleRequestDesignerEdit]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        handleSubmitMessage();
      }
    },
    [handleSubmitMessage]
  );

  // Completed pinning session indicator
  if (hasCompletedSession) {
    return <CompletedSessionIndicator placementKey={placementKey} placementSide={placementSide} />;
  }

  if (!pinBasedRedrawEnabled || hasActiveSession || !isPinningEnabled) {
    return null;
  }

  return (
    <div className="mb-4 overflow-hidden rounded-md border border-blue-200 focus-within:border-blue-600">
      <div className="bg-blue-50 px-3 py-2">
        <span className="text-sm font-semibold text-neutral-950">Get Free Artist Edits</span>
      </div>
      <div className="flex flex-col gap-2 px-3 py-2">
        <Textarea
          data-testid="request-designer-edit-input"
          data-hj-allow
          className="mp-unmask min-h-0 resize-none border-0 p-0 shadow-none focus-visible:ring-0"
          placeholder="Type your edit and we'll guide you through it"
          rows={2}
          value={inputMessage}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={isCreatingPinningSession}
        />
        <div className="flex justify-end">
          <Button
            data-testid="request-designer-edit-submit"
            size="sm"
            className={
              cn(
                "bg-blue-600 hover:bg-blue-700",
                !inputMessage.trim() && !isCreatingPinningSession && "invisible"
              )!
            }
            onPress={handleSubmitMessage}
            isDisabled={isCreatingPinningSession}
          >
            {isCreatingPinningSession ? "Submitting\u2026" : "Submit"}
          </Button>
        </div>
      </div>
    </div>
  );
}
